import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import CTAButton from '../buttons/CTAButton';
import SecondaryButton from '../buttons/SecondaryButton';
import './navbar.css';

const primaryLinks = [
  { to: '/', label: 'Home' },
  { to: '/search', label: 'Demo Shop' },
];

const accountLinks = [
  { to: '/profile', label: 'Profile' },
  { to: '/orders', label: 'Orders' },
  { to: '/favourites', label: 'Favourites' },
  { to: '/help', label: 'Help' },
  { to: '/settings', label: 'Settings' },
];

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    const closeOnClick = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setProfileOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } finally {
      setProfileOpen(false);
      setMobileOpen(false);
    }
  };

  const initials = user?.user_metadata?.full_name?.slice(0, 1) || user?.email?.slice(0, 1) || 'K';
  const userLabel = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Kinn member';

  const toggleMobile = () => setMobileOpen((prev) => !prev);

  const renderNavLink = (link) => (
    <NavLink
      key={link.to}
      to={link.to}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) =>
        [
          'kinn-nav__link',
          isActive ? 'kinn-nav__link--active' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {link.label}
    </NavLink>
  );

  return (
    <>
      <header className={['kinn-nav', isScrolled ? 'kinn-nav--scrolled' : ''].join(' ').trim()}>
        <div className="kinn-nav__inner">
          <NavLink to="/" className="kinn-nav__brand" onClick={() => setMobileOpen(false)}>
            <span className="kinn-nav__logo">
              <img src="/logo/icon.svg" alt="Kinn logo" />
            </span>
            <div>
              <span className="kinn-nav__brand-name">Kinn</span>
              <span className="kinn-nav__tagline">Nourish • Create • Connect</span>
            </div>
          </NavLink>

          <nav className="kinn-nav__links">
            {primaryLinks.map(renderNavLink)}
          </nav>

          <div className="kinn-nav__actions">
            {isAuthenticated ? (
              <>
                <NavLink
                  to="/cart"
                  className="kinn-nav__icon-button"
                  aria-label="Cart"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 5h2l1.68 7.59a2 2 0 001.97 1.58h7.82a2 2 0 001.97-1.58L19 7H6"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="10" cy="19" r="1.25" fill="currentColor" />
                    <circle cx="16" cy="19" r="1.25" fill="currentColor" />
                  </svg>
                </NavLink>
                <div className="kinn-nav__profile" ref={profileRef}>
                  <button
                    type="button"
                    className="kinn-nav__avatar"
                    onClick={() => setProfileOpen((prev) => !prev)}
                    aria-haspopup="true"
                    aria-expanded={profileOpen}
                  >
                    {initials.toUpperCase()}
                  </button>
                  {profileOpen && (
                    <div className="kinn-nav__dropdown">
                      <div className="kinn-nav__dropdown-header">
                        <p>{userLabel}</p>
                        <span>{user?.email}</span>
                      </div>
                      <div className="kinn-nav__dropdown-links">
                        {accountLinks.map((link) => (
                          <NavLink
                            key={link.to}
                            to={link.to}
                            onClick={() => {
                              setProfileOpen(false);
                              setMobileOpen(false);
                            }}
                          >
                            {link.label}
                          </NavLink>
                        ))}
                      </div>
                      <button type="button" onClick={handleLogout} className="kinn-nav__dropdown-logout">
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <SecondaryButton as={NavLink} to="/auth/login" size="sm" className="hidden md:inline-flex" onClick={() => setMobileOpen(false)}>
                Log in
              </SecondaryButton>
            )}

            {!isAuthenticated && (
              <CTAButton
                size="sm"
                as={NavLink}
                to="/seller/get-started"
                className="hidden md:inline-flex"
                onClick={() => setMobileOpen(false)}
              >
                Get Started
              </CTAButton>
            )}

            <button
              type="button"
              className="kinn-nav__mobile-toggle md:hidden"
              aria-label="Toggle navigation"
              onClick={toggleMobile}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                {mobileOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                ) : (
                  <>
                    <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M4 12h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        <div className={['kinn-nav__mobile-panel', mobileOpen ? 'is-open' : ''].join(' ')}>
          <nav className="kinn-nav__mobile-links">
            {primaryLinks.map(renderNavLink)}
          </nav>
          <div className="kinn-nav__mobile-actions">
            {isAuthenticated ? (
              <div className="kinn-nav__mobile-account">
                {accountLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className="kinn-nav__mobile-account-link"
                  >
                    {link.label}
                  </NavLink>
                ))}
                <button type="button" onClick={handleLogout} className="kinn-nav__mobile-logout">
                  Log out
                </button>
              </div>
            ) : (
              <SecondaryButton as={NavLink} to="/auth/login" fullWidth size="md" onClick={() => setMobileOpen(false)}>
                Log in
              </SecondaryButton>
            )}
            {!isAuthenticated && (
              <CTAButton
                fullWidth
                size="md"
                as={NavLink}
                to="/seller/get-started"
                className="mt-4"
                onClick={() => setMobileOpen(false)}
              >
                Get Started
              </CTAButton>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
